import React, { useEffect, useState } from 'react';
import { collection, query, where, orderBy, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

// 报销状态枚举
enum ReimbursementStatus {
    PENDING = "pending", // 待审核
    APPROVED = "approved", // 已批准
    REJECTED = "rejected", // 已拒绝
    PAID = "paid" // 已支付
}

// 报销类型枚举
enum ReimbursementType {
    TEACHING_MATERIALS = "teaching_materials", // 教学材料
    TRAVEL = "travel", // 差旅费
    OFFICE_SUPPLIES = "office_supplies", // 办公用品
    EQUIPMENT = "equipment", // 设备
    OTHER = "other" // 其他
}

// 报销记录接口
interface Reimbursement {
    id: string;
    title: string;
    description: string;
    amount: number;
    type: ReimbursementType;
    receipts: string[];
    additionalDocuments?: string[];
    status: ReimbursementStatus;
    reviewComment?: string;
    reviewedBy?: any;
    reviewedAt?: Date;
    paymentMethod?: string;
    bankAccount?: {
        accountName: string;
        accountNumber: string;
        bankName: string;
    };
    paymentDate?: Date;
    createdBy: any;
    createdAt: Date;
    updatedAt: Date;
}

const TeacherReimbursements: React.FC = () => {
    const [reimbursements, setReimbursements] = useState<Reimbursement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const currentLocale = i18n.language === 'zh' ? zhCN : enUS;

    useEffect(() => {
        const fetchReimbursements = async () => {
            if (!auth.currentUser) return;
            
            try {
                setLoading(true);
                const userRef = doc(db, 'users', auth.currentUser.uid);
                
                // 查询当前用户创建的报销记录，限制为最新的5条
                const q = query(
                    collection(db, 'reimbursements'),
                    where('createdBy', '==', userRef),
                    orderBy('createdAt', 'desc'),
                    limit(5) // 只获取最新的5条记录
                );
                
                const querySnapshot = await getDocs(q);
                const reimbursementsList: Reimbursement[] = [];
                
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    reimbursementsList.push({
                        id: doc.id,
                        ...data,
                        createdAt: data.createdAt?.toDate(),
                        updatedAt: data.updatedAt?.toDate(),
                        reviewedAt: data.reviewedAt?.toDate(),
                        paymentDate: data.paymentDate?.toDate(),
                    } as Reimbursement);
                });
                
                setReimbursements(reimbursementsList);
            } catch (err) {
                console.error('获取报销记录失败:', err);
                setError(t('reimbursements.fetchError'));
            } finally {
                setLoading(false);
            }
        };
        
        fetchReimbursements();
    }, [t]);

    // 获取报销类型的本地化名称
    const getReimbursementTypeName = (type: ReimbursementType) => {
        switch (type) {
            case ReimbursementType.TEACHING_MATERIALS:
                return t('reimbursements.types.teaching_materials');
            case ReimbursementType.TRAVEL:
                return t('reimbursements.types.travel');
            case ReimbursementType.OFFICE_SUPPLIES:
                return t('reimbursements.types.office_supplies');
            case ReimbursementType.EQUIPMENT:
                return t('reimbursements.types.equipment');
            case ReimbursementType.OTHER:
                return t('reimbursements.types.other');
            default:
                return type;
        }
    };

    // 获取报销状态的本地化名称和样式
    const getStatusInfo = (status: ReimbursementStatus) => {
        switch (status) {
            case ReimbursementStatus.PENDING:
                return {
                    name: t('reimbursements.status.pending'),
                    bgColor: 'bg-yellow-100',
                    textColor: 'text-yellow-800',
                    borderColor: 'border-yellow-200'
                };
            case ReimbursementStatus.APPROVED:
                return {
                    name: t('reimbursements.status.approved'),
                    bgColor: 'bg-green-100',
                    textColor: 'text-green-800',
                    borderColor: 'border-green-200'
                };
            case ReimbursementStatus.REJECTED:
                return {
                    name: t('reimbursements.status.rejected'),
                    bgColor: 'bg-red-100',
                    textColor: 'text-red-800',
                    borderColor: 'border-red-200'
                };
            case ReimbursementStatus.PAID:
                return {
                    name: t('reimbursements.status.paid'),
                    bgColor: 'bg-blue-100',
                    textColor: 'text-blue-800',
                    borderColor: 'border-blue-200'
                };
            default:
                return {
                    name: status,
                    bgColor: 'bg-gray-100',
                    textColor: 'text-gray-800',
                    borderColor: 'border-gray-200'
                };
        }
    };

    // 格式化日期
    const formatDate = (date: Date) => {
        if (!date) return '';
        return format(date, 'PPP', { locale: currentLocale });
    };

    if (loading) {
        return (
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/20 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">{t('reimbursements.title')}</h2>
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-transparent"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-white/20 p-6">
                <h2 className="text-2xl font-bold text-white mb-4">{t('reimbursements.title')}</h2>
                <div className="bg-red-100/20 backdrop-blur-sm border border-red-200/30 rounded-lg p-4 text-red-100">
                    <p>{error}</p>
                    <button 
                        onClick={() => window.location.reload()} 
                        className="mt-2 px-4 py-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-md text-red-100 transition-all"
                    >
                        {t('common.retry')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/20 p-6">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{t('reimbursements.title')}</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => navigate('/profile/reimbursements/new')}
                        className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-md text-blue-100 transition-all flex items-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                        {t('reimbursements.addNew')}
                    </button>
                    <button
                        onClick={() => navigate('/profile/reimbursements/all')}
                        className="px-4 py-2 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 rounded-md text-purple-100 transition-all flex items-center"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                        </svg>
                        {t('reimbursements.viewAll')}
                    </button>
                </div>
            </div>

            {reimbursements.length === 0 ? (
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-8 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-blue-300/50 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-blue-100 mb-4">{t('reimbursements.noRecords')}</p>
                    <button
                        onClick={() => navigate('/profile/reimbursements/new')}
                        className="px-4 py-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-md text-blue-100 transition-all"
                    >
                        {t('reimbursements.createFirst')}
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {reimbursements.map((reimbursement, index) => {
                        const statusInfo = getStatusInfo(reimbursement.status);
                        
                        return (
                            <motion.div
                                key={reimbursement.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-4 hover:bg-white/10 transition-all cursor-pointer"
                                onClick={() => navigate(`/profile/reimbursements/${reimbursement.id}`)}
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center">
                                            <h3 className="text-lg font-semibold text-white">{reimbursement.title}</h3>
                                            <span className={`ml-3 px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bgColor} ${statusInfo.textColor} border ${statusInfo.borderColor}`}>
                                                {statusInfo.name}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-sm text-blue-100">
                                            <span className="inline-block mr-4">
                                                <span className="opacity-70">{t('reimbursements.amount')}:</span> ${reimbursement.amount.toFixed(2)}
                                            </span>
                                            <span className="inline-block mr-4">
                                                <span className="opacity-70">{t('reimbursements.type')}:</span> {getReimbursementTypeName(reimbursement.type)}
                                            </span>
                                            <span className="inline-block">
                                                <span className="opacity-70">{t('reimbursements.createdAt')}:</span> {formatDate(reimbursement.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="mt-2 md:mt-0">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-300/70" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default TeacherReimbursements; 